import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

const TimePicker = ({ value, onChange, placeholder = "Select time", disabled = false, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(null);
  const [selectedMinute, setSelectedMinute] = useState(null);
  const [position, setPosition] = useState({});
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useLayoutEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      setSelectedHour(parseInt(h, 10));
      setSelectedMinute(parseInt(m, 10));
    } else {
      setSelectedHour(null);
      setSelectedMinute(null);
    }
  }, [value]);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const updatePosition = () => {
      const trigger = triggerRef.current.getBoundingClientRect();
      const popoverWidth = 256; 
      const popoverHeight = 280; 
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = trigger.bottom + 4;
      let left = trigger.left;
      
      if (top + popoverHeight > viewportHeight && trigger.top > popoverHeight) {
        top = trigger.top - popoverHeight - 4;
      }

      if (left + popoverWidth > viewportWidth) {
        left = trigger.right - popoverWidth;
      }
      
      if (left < 4) left = 4;

      setPosition({ top, left });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const handleClickOutside = (e) => {
    if (
      popoverRef.current && 
      !popoverRef.current.contains(e.target) &&
      triggerRef.current && 
      !triggerRef.current.contains(e.target)
    ) {
      setIsOpen(false);
    }
  };

  useLayoutEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleTimeSelect = (type, val) => {
    let newH = selectedHour;
    let newM = selectedMinute;

    if (type === 'hour') newH = val;
    if (type === 'minute') newM = val;

    setSelectedHour(newH);
    setSelectedMinute(newM);

    if (newH !== null && newM !== null) {
      const formattedH = String(newH).padStart(2, '0');
      const formattedM = String(newM).padStart(2, '0');
      onChange(`${formattedH}:${formattedM}`);
    }
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-full px-4 py-3 text-sm bg-white border rounded-[6px] transition-all
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:bg-gray-50'}
          ${isOpen ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-300'}
          ${!value ? 'text-gray-400' : 'text-gray-900'}
          ${className}
        `}
      >
        <span className="truncate">{value || placeholder}</span>
        <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {isOpen && createPortal(
        <div
          ref={popoverRef}
          className="fixed z-[9999] w-64 bg-white border border-gray-200 rounded-[6px] shadow-xl animate-in fade-in zoom-in-95 overflow-hidden flex flex-col"
          style={{ 
            top: `${position.top}px`, 
            left: `${position.left}px`,
            maxHeight: '280px' 
          }}
        >
          <div className="flex flex-1 min-h-0 overflow-hidden">
            <div className="flex flex-col flex-1 border-r border-gray-100">
              <div className="p-2 text-xs font-semibold text-center text-gray-500 border-b border-gray-100 bg-gray-50 shrink-0">Hour</div>
              <div className="flex-1 p-1 space-y-1 overflow-y-auto scrollbar-thin">
                {hours.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => handleTimeSelect('hour', h)}
                    className={`w-full py-1.5 text-sm rounded-[4px] transition-colors ${
                      selectedHour === h ? 'bg-blue-600 text-white font-medium' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {String(h).padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col flex-1">
              <div className="p-2 text-xs font-semibold text-center text-gray-500 border-b border-gray-100 bg-gray-50 shrink-0">Minute</div>
              <div className="flex-1 p-1 space-y-1 overflow-y-auto scrollbar-thin">
                {minutes.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => handleTimeSelect('minute', m)}
                    className={`w-full py-1.5 text-sm rounded-[4px] transition-colors ${
                      selectedMinute === m ? 'bg-blue-600 text-white font-medium' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {String(m).padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="p-2 text-center border-t border-gray-100 bg-gray-50 shrink-0">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Done
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default TimePicker;
